import React, { useState, useCallback } from 'react';
import { 
  Typography, 
  Paper, 
  Box, 
  TextField, 
  Select, 
  MenuItem, 
  FormControlLabel, 
  Checkbox, 
  Slider, 
  Button, 
  Stepper, 
  Step, 
  StepLabel, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  IconButton,
  LinearProgress, 
  Grid,  
  Chip,
  Tooltip,
  CardMedia,
  Container,
  Avatar,
  InputLabel,
  FormControl,
  Switch,
  StepConnector,
  styled,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useNotification } from '../../contexts/NotificationContext';
import { generateCourse, saveCourse } from '../../services/api';
import LessonCreatorAuto from '../LessonCreatorAuto/LessonCreatorAuto';
import TestCreator from '../TestCreator/TestCreator';
import { useNavigate } from 'react-router-dom';
import { compressImage, processImagesInContent } from '../../utils/imageUtils';
import InfoIcon from '@mui/icons-material/Info';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import TuneIcon from '@mui/icons-material/Tune';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

// Paleta profesional verde-azul + morado para IA
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  orange: '#e17055',
  dark: '#2d3436',
  light: '#dfe6e9',
  ai: '#a29bfe'
};

// Colores por categoría
const CATEGORY_COLORS = {
  'Mantenimiento': { color: COLORS.warning, icon: '🔧' },
  'Producción': { color: COLORS.success, icon: '🏭' },
  'Calidad': { color: COLORS.primary, icon: '✅' },
  'Seguridad': { color: COLORS.error, icon: '🛡️' },
  'General': { color: COLORS.orange, icon: '📖' }
};

// Styled Step Connector con morado para IA
const CustomConnector = styled(StepConnector)(() => ({
  '& .MuiStepConnector-line': {
    borderColor: '#e0e0e0',
    borderTopWidth: 3,
    borderRadius: 1,
  },
  '&.Mui-active .MuiStepConnector-line': {
    background: `linear-gradient(90deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`,
    borderColor: 'transparent',
    height: 3,
  },
  '&.Mui-completed .MuiStepConnector-line': {
    background: `linear-gradient(90deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`,
    borderColor: 'transparent',
    height: 3,
  },
}));

const AutoCourseGenerator = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [courseData, setCourseData] = useState({
    title: '',
    category: '',
    generateQuestions: false,
    questionCount: 5,
    passingScore: 70,
    pdfsForGeneration: [],
    imagesForGeneration: [],
    videosForGeneration: [],
    coverImage: null,
    attachedPdfs: [],
    attachedImages: [],
    attachedVideos: [],
    testConfig: {
      basicQuestions: 10,
      intermediateQuestions: 6,
      advancedQuestions: 4,
      questions_per_test: 2,
      rotateQuestions: true,
      requireAllDifficulties: true
    }
  });
  const [generatedCourse, setGeneratedCourse] = useState(null);
  const { showNotification } = useNotification();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryFiles, setLibraryFiles] = useState({ pdfs: [], images: [], covers: [] });
  const [libraryTarget, setLibraryTarget] = useState('');
  const [libraryLoading, setLibraryLoading] = useState(false);

  const steps = [
    { label: 'Configuración', icon: <SettingsIcon /> },
    { label: 'Editar Curso', icon: <EditIcon /> },
    { label: 'Revisión', icon: <CheckCircleIcon /> }
  ];

  const fetchLibrary = async () => {
    setLibraryLoading(true);
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + '/library');
      setLibraryFiles(await res.json());
    } catch (e) { console.error('Error cargando biblioteca:', e); }
    finally { setLibraryLoading(false); }
  };

  const openLibrary = (target) => {
    setLibraryTarget(target);
    setLibraryOpen(true);
    fetchLibrary();
  };

  const handleLibrarySelect = async (file) => {
    try {
      const res = await fetch(file.url);
      const blob = await res.blob();
      if (libraryTarget === 'coverImage') {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const compressed = await compressImage(reader.result);
          setCourseData(prev => ({ ...prev, coverImage: compressed }));
        };
        reader.readAsDataURL(blob);
      } else {
        const fileObj = new File([blob], file.name, { type: blob.type });
        setCourseData(prev => ({ ...prev, [libraryTarget]: [...prev[libraryTarget], fileObj] }));
      }
      showNotification('Archivo a\u00f1adido: ' + file.name, 'success');
      setLibraryOpen(false);
    } catch (e) {
      showNotification('Error al cargar archivo', 'error');
    }
  };

  const getLibraryFilesForTarget = () => {
    if (['pdfsForGeneration', 'attachedPdfs'].includes(libraryTarget)) return libraryFiles.pdfs || [];
    if (['imagesForGeneration', 'attachedImages', 'coverImage'].includes(libraryTarget))
      return [...(libraryFiles.covers || []), ...(libraryFiles.images || [])];
    return [...(libraryFiles.pdfs || []), ...(libraryFiles.images || []), ...(libraryFiles.covers || [])];
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleInputChange = (event) => {
    const { name, value, checked, type } = event.target;
    if (name.startsWith('testConfig.')) {
      const configField = name.split('.')[1];
      setCourseData(prev => ({
        ...prev,
        testConfig: {
          ...prev.testConfig,
          [configField]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setCourseData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleFileUpload = (event, fileType) => {
    const files = Array.from(event.target.files);
    setCourseData(prev => ({
      ...prev,
      [fileType]: [...prev[fileType], ...files]
    }));
  };

  const handleFileRemove = (index, fileType) => {
    setCourseData(prev => ({
      ...prev,
      [fileType]: prev[fileType].filter((_, i) => i !== index)
    }));
  };

  const handleCoverImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const compressedImage = await compressImage(reader.result);
        setCourseData(prev => ({ ...prev, coverImage: compressedImage }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverImageRemove = () => {
    setCourseData(prev => ({ ...prev, coverImage: null }));
  };

  const handleGenerateCourse = async () => {
    try {
      if (!courseData.title || !courseData.category) {
        showNotification('El título y la categoría son obligatorios', 'error');
        return;
      }
  
      setIsGenerating(true);
      setProgress(0);
  
      const formData = new FormData();
      formData.append('title', courseData.title);
      formData.append('category', courseData.category);
      formData.append('generateQuestions', courseData.generateQuestions);
      formData.append('min_passing_score', courseData.passingScore);
  
      const testConfigData = {
        questions_per_test: parseInt(courseData.testConfig.questions_per_test),
        basicQuestions: parseInt(courseData.testConfig.basicQuestions),
        intermediateQuestions: parseInt(courseData.testConfig.intermediateQuestions),
        advancedQuestions: parseInt(courseData.testConfig.advancedQuestions),
        rotateQuestions: courseData.testConfig.rotateQuestions,
        requireAllDifficulties: courseData.testConfig.requireAllDifficulties
      };
      formData.append('testConfig', JSON.stringify(testConfigData));
  
      courseData.pdfsForGeneration.forEach(pdf => formData.append('pdfsForGeneration', pdf));
      courseData.imagesForGeneration.forEach(image => formData.append('imagesForGeneration', image));
      courseData.videosForGeneration.forEach(video => formData.append('videosForGeneration', video));
  
      if (courseData.coverImage) {
        if (typeof courseData.coverImage === 'string' && courseData.coverImage.startsWith('data:')) {
          const response = await fetch(courseData.coverImage);
          const blob = await response.blob();
          formData.append('coverImage', blob, 'cover.jpg');
        } else {
          formData.append('coverImage', courseData.coverImage);
        }
      }
  
      courseData.attachedPdfs.forEach(pdf => formData.append('attachedPdfs', pdf));
      courseData.attachedImages.forEach(image => formData.append('attachedImages', image));
      courseData.attachedVideos.forEach(video => formData.append('attachedVideos', video));
  
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 1000);

      const response = await generateCourse(formData);
      clearInterval(progressInterval);
      setProgress(100);

      const generatedCourseData = {
        ...response.data,
        test: {
          questions: response.data.test?.questions || [],
          config: {
            questions_per_test: courseData.testConfig.questions_per_test,
            rotateQuestions: courseData.testConfig.rotateQuestions,
            requireAllDifficulties: courseData.testConfig.requireAllDifficulties
          }
        }
      };
  
      setGeneratedCourse(generatedCourseData);
      showNotification('Curso generado con éxito', 'success');
      setActiveStep(prev => prev + 1);
    } catch (error) {
      console.error('Error al generar el curso:', error);
      showNotification(error.response?.data?.message || 'Error al generar el curso', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleLessonsChange = useCallback((newLessons) => {
    setGeneratedCourse(prevCourse => {
      if (JSON.stringify(prevCourse.lessons) !== JSON.stringify(newLessons)) {
        return { ...prevCourse, lessons: newLessons };
      }
      return prevCourse;
    });
  }, []);

  const handleTestChange = (newTest) => {
    setGeneratedCourse(prev => ({ 
      ...prev, 
      test: {
        questions: newTest.questions || [],
        config: { ...prev.test?.config, ...newTest.config }
      } 
    }));
  };

  const handleSaveCourse = async () => {
    try {
      const processedLessons = await Promise.all(generatedCourse.lessons.map(async (lesson) => {
        const processedContent = await processImagesInContent(lesson.content);
        return { ...lesson, content: processedContent };
      }));
  
      const formData = new FormData();
      formData.append('title', generatedCourse.title);
      formData.append('description', generatedCourse.description);
      formData.append('category', generatedCourse.category);
      formData.append('min_passing_score', generatedCourse.min_passing_score);
      formData.append('lessons', JSON.stringify(processedLessons));
      formData.append('test', JSON.stringify(generatedCourse.test));
  
      if (courseData.coverImage) {
        const coverImageBlob = await fetch(courseData.coverImage).then(r => r.blob());
        formData.append('coverImage', coverImageBlob, 'cover.jpg');
      }
  
      courseData.attachedPdfs.forEach(pdf => formData.append('coursePdf', pdf));
      courseData.attachedImages.forEach(image => formData.append('courseImage', image));
      courseData.attachedVideos.forEach(video => formData.append('courseVideo', video));
  
      await saveCourse(formData);
      showNotification('Curso guardado con éxito', 'success');
      navigate('/courses');
    } catch (error) {
      console.error('Error al guardar el curso:', error);
      showNotification('Error al guardar el curso', 'error');
    }
  };

  const renderFileChips = (fileType, label, icon, color) => {
    const files = courseData[fileType] || [];
    if (files.length === 0) return null;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 18 } })}
          <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.dark }}>{label}</Typography>
          <Chip label={files.length} size="small" sx={{ bgcolor: alpha(color, 0.15), color, fontWeight: 600, height: 20 }} />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {files.map((file, index) => (
            <Chip
              key={index}
              label={file.name}
              onDelete={() => handleFileRemove(index, fileType)}
              size="small"
              sx={{ bgcolor: alpha(color, 0.1), color, '& .MuiChip-deleteIcon': { color: alpha(color, 0.7) } }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: 3, background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 8px 24px ${alpha(COLORS.purple, 0.3)}` }}>
                <SmartToyIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.dark }}>Configuración del Curso</Typography>
                <Typography variant="body2" sx={{ color: '#636e72' }}>Define los parámetros para la generación automática con IA</Typography>
              </Box>
            </Box>

            {/* Información básica */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px solid #e0e0e0', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <InfoIcon sx={{ color: COLORS.primary }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>Información Básica</Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Título del curso"
                    name="title"
                    value={courseData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Seguridad en el Trabajo"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Categoría *</InputLabel>
                    <Select label="Categoría *" name="category" value={courseData.category} onChange={handleInputChange} sx={{ borderRadius: 2 }}>
                      {Object.entries(CATEGORY_COLORS).map(([cat, config]) => (
                        <MenuItem key={cat} value={cat}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{config.icon}</span>
                            <Typography>{cat}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {courseData.category && (
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={`${CATEGORY_COLORS[courseData.category]?.icon} ${courseData.category}`}
                    sx={{ bgcolor: alpha(CATEGORY_COLORS[courseData.category]?.color, 0.15), color: CATEGORY_COLORS[courseData.category]?.color, fontWeight: 700, fontSize: '0.9rem', py: 2 }}
                  />
                </Box>
              )}

              {/* Slider puntuación */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.dark, mb: 1 }}>Puntuación mínima para aprobar</Typography>
                <Box sx={{ px: 2 }}>
                  <Slider
                    value={courseData.passingScore}
                    onChange={(e, newValue) => handleInputChange({ target: { name: 'passingScore', value: newValue }})}
                    valueLabelDisplay="on"
                    step={5}
                    marks={[{ value: 0, label: '0%' }, { value: 50, label: '50%' }, { value: 70, label: '70%' }, { value: 100, label: '100%' }]}
                    min={0}
                    max={100}
                    sx={{
                      color: COLORS.primary,
                      height: 8,
                      '& .MuiSlider-thumb': { width: 24, height: 24, backgroundColor: 'white', border: `3px solid ${COLORS.primary}` },
                      '& .MuiSlider-track': { background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`, border: 'none' },
                      '& .MuiSlider-valueLabel': { background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`, borderRadius: 2, fontWeight: 700 }
                    }}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Configuración de preguntas */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px solid #e0e0e0', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <QuizIcon sx={{ color: COLORS.purple }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>Generación de Preguntas</Typography>
                </Box>
                <FormControlLabel
                  control={<Switch checked={courseData.generateQuestions} onChange={handleInputChange} name="generateQuestions" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: COLORS.purple }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: COLORS.purple } }} />}
                  label={<Chip icon={<AutoAwesomeIcon />} label="Generar con IA" size="small" sx={{ bgcolor: courseData.generateQuestions ? alpha(COLORS.purple, 0.15) : '#f5f5f5', color: courseData.generateQuestions ? COLORS.purple : '#9e9e9e', fontWeight: 600, '& .MuiChip-icon': { color: courseData.generateQuestions ? COLORS.purple : '#9e9e9e' } }} />}
                />
              </Box>

              {courseData.generateQuestions && (
                <Box sx={{ p: 3, borderRadius: 2, bgcolor: alpha(COLORS.purple, 0.05), border: `1px solid ${alpha(COLORS.purple, 0.2)}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <TuneIcon sx={{ color: COLORS.purple }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.dark }}>Distribución de Preguntas</Typography>
                    <Tooltip title="Configura cuántas preguntas de cada dificultad se generarán">
                      <IconButton size="small"><InfoIcon fontSize="small" sx={{ color: COLORS.purple }} /></IconButton>
                    </Tooltip>
                  </Box>

                  <Grid container spacing={2}>
                    {[
                      { key: 'basicQuestions', label: 'Básico', color: COLORS.success },
                      { key: 'intermediateQuestions', label: 'Intermedio', color: COLORS.warning },
                      { key: 'advancedQuestions', label: 'Avanzado', color: COLORS.error }
                    ].map(({ key, label, color }) => (
                      <Grid item xs={12} sm={4} key={key}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `2px solid ${alpha(color, 0.3)}`, bgcolor: alpha(color, 0.05), textAlign: 'center' }}>
                          <Chip label={label} size="small" sx={{ bgcolor: alpha(color, 0.15), color, fontWeight: 700, mb: 1 }} />
                          <TextField
                            fullWidth
                            type="number"
                            value={courseData.testConfig[key]}
                            onChange={(e) => handleInputChange({ target: { name: `testConfig.${key}`, value: Math.max(0, parseInt(e.target.value) || 0) } })}
                            inputProps={{ min: 0, style: { textAlign: 'center', fontWeight: 700, fontSize: '1.2rem' } }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '& fieldset': { borderColor: alpha(color, 0.3) } } }}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<QuizIcon />}
                      label={`Total: ${(courseData.testConfig.basicQuestions || 0) + (courseData.testConfig.intermediateQuestions || 0) + (courseData.testConfig.advancedQuestions || 0)} preguntas`}
                      sx={{ bgcolor: alpha(COLORS.purple, 0.15), color: COLORS.purple, fontWeight: 700, '& .MuiChip-icon': { color: COLORS.purple } }}
                    />
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark, mb: 1 }}>Preguntas por test</Typography>
                    <TextField
                      type="number"
                      value={courseData.testConfig.questions_per_test}
                      onChange={(e) => handleInputChange({ target: { name: 'testConfig.questions_per_test', value: Math.max(2, Math.min(parseInt(e.target.value) || 2, 50)) } })}
                      inputProps={{ min: 2 }}
                      helperText="Número de preguntas que verá cada usuario (mín. 2)"
                      sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={<Checkbox checked={courseData.testConfig.rotateQuestions} onChange={(e) => handleInputChange({ target: { name: 'testConfig.rotateQuestions', value: e.target.checked, type: 'checkbox' } })} sx={{ color: COLORS.purple, '&.Mui-checked': { color: COLORS.purple } }} />}
                      label={<Typography sx={{ fontWeight: 500 }}>Rotar preguntas aleatoriamente</Typography>}
                    />
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Archivos para generación */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px dashed #e0e0e0', mb: 3, bgcolor: alpha(COLORS.primary, 0.02) }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <RocketLaunchIcon sx={{ color: COLORS.primary }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>Archivos para Generación</Typography>
                  <Typography variant="body2" sx={{ color: '#636e72' }}>La IA analizará estos archivos para crear el contenido</Typography>
                </Box>
              </Box>

              {/* Biblioteca del Servidor */}
              <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: alpha(COLORS.purple, 0.05), border: '1px dashed ' + alpha(COLORS.purple, 0.3), display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FolderOpenIcon sx={{ color: COLORS.purple, fontSize: 28 }} />
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark }}>Biblioteca del Servidor</Typography>
                  <Typography variant="caption" sx={{ color: '#636e72' }}>Archivos pre-cargados para demos</Typography>
                </Box>
                <Button variant="outlined" size="small" startIcon={<CloudDownloadIcon />} onClick={() => openLibrary('pdfsForGeneration')} sx={{ borderColor: COLORS.error, color: COLORS.error, borderRadius: 2, '&:hover': { borderColor: COLORS.error, bgcolor: alpha(COLORS.error, 0.08) } }}>
                  PDFs
                </Button>
                <Button variant="outlined" size="small" startIcon={<CloudDownloadIcon />} onClick={() => openLibrary('coverImage')} sx={{ borderColor: COLORS.purple, color: COLORS.purple, borderRadius: 2, '&:hover': { borderColor: COLORS.purple, bgcolor: alpha(COLORS.purple, 0.08) } }}>
                  Portadas
                </Button>
                <Button variant="outlined" size="small" startIcon={<CloudDownloadIcon />} onClick={() => openLibrary('imagesForGeneration')} sx={{ borderColor: COLORS.success, color: COLORS.success, borderRadius: 2, '&:hover': { borderColor: COLORS.success, bgcolor: alpha(COLORS.success, 0.08) } }}>
                  Imágenes
                </Button>
              </Box>

              {/* Portada */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark, mb: 1 }}>Imagen de Portada</Typography>
                {courseData.coverImage ? (
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img src={courseData.coverImage} alt="Portada" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, border: '2px solid #e0e0e0' }} />
                    <IconButton onClick={handleCoverImageRemove} size="small" sx={{ position: 'absolute', top: -10, right: -10, bgcolor: COLORS.error, color: 'white', '&:hover': { bgcolor: alpha(COLORS.error, 0.8) } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box>
                    <input accept="image/*" style={{ display: 'none' }} id="cover-image" type="file" onChange={handleCoverImageUpload} />
                    <label htmlFor="cover-image">
                      <Button variant="outlined" component="span" startIcon={<ImageIcon />} sx={{ borderColor: COLORS.purple, color: COLORS.purple, borderRadius: 2, '&:hover': { borderColor: COLORS.purple, bgcolor: alpha(COLORS.purple, 0.08) } }}>
                        Subir Portada
                      </Button>
                    </label>
                  </Box>
                )}
              </Box>

              {/* Botones upload */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                {[
                  { id: 'pdfsForGeneration', accept: '.pdf', icon: <PictureAsPdfIcon />, label: 'PDFs', color: COLORS.error },
                  { id: 'imagesForGeneration', accept: 'image/*', icon: <ImageIcon />, label: 'Imágenes', color: COLORS.success },
                  { id: 'videosForGeneration', accept: 'video/*', icon: <VideoLibraryIcon />, label: 'Videos', color: COLORS.primary }
                ].map(({ id, accept, icon, label, color }) => (
                  <Box key={id}>
                    <input accept={accept} style={{ display: 'none' }} id={id} multiple type="file" onChange={(e) => handleFileUpload(e, id)} />
                    <label htmlFor={id}>
                      <Button variant="outlined" component="span" startIcon={icon} sx={{ borderColor: color, color, borderRadius: 2, '&:hover': { borderColor: color, bgcolor: alpha(color, 0.08) } }}>
                        {label}
                      </Button>
                    </label>
                  </Box>
                ))}
              </Box>

              {renderFileChips('pdfsForGeneration', 'PDFs para análisis', <PictureAsPdfIcon />, COLORS.error)}
              {renderFileChips('imagesForGeneration', 'Imágenes para análisis', <ImageIcon />, COLORS.success)}
              {renderFileChips('videosForGeneration', 'Videos para análisis', <VideoLibraryIcon />, COLORS.primary)}
            </Paper>

            {/* Archivos adjuntos */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <AttachFileIcon sx={{ color: COLORS.info }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>Archivos Adjuntos</Typography>
                  <Typography variant="body2" sx={{ color: '#636e72' }}>Se adjuntarán al curso (no se analizarán)</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                {[
                  { id: 'attachedPdfs', accept: '.pdf', icon: <PictureAsPdfIcon />, label: 'PDFs' },
                  { id: 'attachedImages', accept: 'image/*', icon: <ImageIcon />, label: 'Imágenes' },
                  { id: 'attachedVideos', accept: 'video/*', icon: <VideoLibraryIcon />, label: 'Videos' }
                ].map(({ id, accept, icon, label }) => (
                  <Box key={id}>
                    <input accept={accept} style={{ display: 'none' }} id={id} multiple type="file" onChange={(e) => handleFileUpload(e, id)} />
                    <label htmlFor={id}>
                      <Button variant="outlined" component="span" size="small" startIcon={icon} sx={{ borderColor: '#bdbdbd', color: '#636e72', borderRadius: 2 }}>
                        {label}
                      </Button>
                    </label>
                  </Box>
                ))}
              </Box>

              {renderFileChips('attachedPdfs', 'PDFs adjuntos', <PictureAsPdfIcon />, '#636e72')}
              {renderFileChips('attachedImages', 'Imágenes adjuntas', <ImageIcon />, '#636e72')}
              {renderFileChips('attachedVideos', 'Videos adjuntos', <VideoLibraryIcon />, '#636e72')}
            </Paper>
          </Box>
        );

      case 1:
        return generatedCourse ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: 3, background: `linear-gradient(135deg, ${COLORS.warning} 0%, ${COLORS.orange} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 8px 24px ${alpha(COLORS.warning, 0.3)}` }}>
                <EditIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.dark }}>Editar Curso Generado</Typography>
                <Typography variant="body2" sx={{ color: '#636e72' }}>Revisa y modifica el contenido generado por la IA</Typography>
              </Box>
            </Box>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px solid #e0e0e0', mb: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Título del curso" value={generatedCourse.title} onChange={(e) => setGeneratedCourse({...generatedCourse, title: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={4} label="Descripción del curso" value={generatedCourse.description} onChange={(e) => setGeneratedCourse({...generatedCourse, description: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Grid>
              </Grid>
            </Paper>

            <LessonCreatorAuto lessons={generatedCourse.lessons || []} onChange={handleLessonsChange} />

            <Box sx={{ mt: 3 }}>
              <TestCreator
                test={{ questions: generatedCourse.test?.questions || [], config: { questions_per_test: generatedCourse.test?.config?.questions_per_test || 10, rotateQuestions: generatedCourse.test?.config?.rotateQuestions || true, requireAllDifficulties: generatedCourse.test?.config?.requireAllDifficulties || false } }}
                onChange={handleTestChange}
              />
            </Box>
          </Box>
        ) : null;

      case 2:
        return generatedCourse ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: 3, background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 8px 24px ${alpha(COLORS.gradient1, 0.3)}` }}>
                <CheckCircleIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.dark }}>Revisión Final</Typography>
                <Typography variant="body2" sx={{ color: '#636e72' }}>Verifica el curso antes de guardarlo</Typography>
              </Box>
            </Box>

            {/* Resumen */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px solid #e0e0e0', mb: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.dark, mb: 1 }}>{generatedCourse.title}</Typography>
                  {generatedCourse.category && (
                    <Chip label={`${CATEGORY_COLORS[generatedCourse.category]?.icon || '📖'} ${generatedCourse.category}`} sx={{ bgcolor: alpha(CATEGORY_COLORS[generatedCourse.category]?.color || COLORS.info, 0.15), color: CATEGORY_COLORS[generatedCourse.category]?.color || COLORS.info, fontWeight: 700, mb: 2 }} />
                  )}
                  <Typography sx={{ color: '#636e72', mb: 2 }}>{generatedCourse.description}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip icon={<MenuBookIcon />} label={`${generatedCourse.lessons?.length || 0} lecciones`} sx={{ bgcolor: alpha(COLORS.primary, 0.15), color: COLORS.primary, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.primary } }} />
                    <Chip icon={<QuizIcon />} label={`${generatedCourse.test?.questions?.length || 0} preguntas`} sx={{ bgcolor: alpha(COLORS.purple, 0.15), color: COLORS.purple, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.purple } }} />
                    <Chip icon={<CheckCircleIcon />} label={`Mín. ${generatedCourse.min_passing_score}%`} sx={{ bgcolor: alpha(COLORS.success, 0.15), color: COLORS.success, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.success } }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  {(generatedCourse.coverImagePath || courseData.coverImage) && (
                    <CardMedia
                      component="img"
                      image={courseData.coverImage || `${process.env.REACT_APP_API_URL}/${generatedCourse.coverImagePath?.replace(/\\/g, '/')}`}
                      alt={generatedCourse.title}
                      onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-image.jpg'; }}
                      sx={{ width: '100%', maxHeight: 150, objectFit: 'cover', borderRadius: 2 }}
                    />
                  )}
                </Grid>
              </Grid>
            </Paper>

            {/* Test config */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px solid #e0e0e0', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <QuizIcon sx={{ color: COLORS.purple }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>Configuración del Test</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Básico: ${generatedCourse.test?.questions?.filter(q => q.difficulty === 'basico').length || 0}`} size="small" sx={{ bgcolor: alpha(COLORS.success, 0.15), color: COLORS.success, fontWeight: 600 }} />
                <Chip label={`Intermedio: ${generatedCourse.test?.questions?.filter(q => q.difficulty === 'intermedio').length || 0}`} size="small" sx={{ bgcolor: alpha(COLORS.warning, 0.15), color: COLORS.warning, fontWeight: 600 }} />
                <Chip label={`Avanzado: ${generatedCourse.test?.questions?.filter(q => q.difficulty === 'avanzado').length || 0}`} size="small" sx={{ bgcolor: alpha(COLORS.error, 0.15), color: COLORS.error, fontWeight: 600 }} />
              </Box>
            </Paper>

            {/* Accordions */}
            <Accordion elevation={0} sx={{ borderRadius: '12px !important', border: '2px solid #e0e0e0', mb: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <MenuBookIcon sx={{ color: COLORS.primary }} />
                  <Typography sx={{ fontWeight: 700, color: COLORS.dark }}>Lecciones ({generatedCourse.lessons?.length || 0})</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {generatedCourse.lessons?.map((lesson, index) => (
                  <Accordion key={index} elevation={0} sx={{ bgcolor: '#f8fffe', mb: 1, borderRadius: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(COLORS.primary, 0.15), color: COLORS.primary, fontSize: '0.8rem', fontWeight: 700 }}>{index + 1}</Avatar>
                        <Typography sx={{ fontWeight: 600 }}>{lesson.title}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ '& img': { maxWidth: '100%' } }} dangerouslySetInnerHTML={{ __html: lesson.content }} />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>

            <Accordion elevation={0} sx={{ borderRadius: '12px !important', border: '2px solid #e0e0e0', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <QuizIcon sx={{ color: COLORS.purple }} />
                  <Typography sx={{ fontWeight: 700, color: COLORS.dark }}>Banco de Preguntas ({generatedCourse.test?.questions?.length || 0})</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {generatedCourse.test?.questions?.map((question, index) => (
                  <Accordion key={index} elevation={0} sx={{ bgcolor: '#f8fffe', mb: 1, borderRadius: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, mr: 2 }}>
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>{index + 1}. {question.question}</Typography>
                        <Chip label={question.difficulty} size="small" sx={{ bgcolor: alpha(question.difficulty === 'avanzado' ? COLORS.error : question.difficulty === 'intermedio' ? COLORS.warning : COLORS.success, 0.15), color: question.difficulty === 'avanzado' ? COLORS.error : question.difficulty === 'intermedio' ? COLORS.warning : COLORS.success, fontWeight: 600 }} />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {question.options?.map((option, optIndex) => (
                          <ListItem key={optIndex}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {option === question.correctAnswer ? <CheckCircleOutlineIcon sx={{ color: COLORS.success }} /> : <RadioButtonUncheckedIcon sx={{ color: '#bdbdbd' }} />}
                            </ListItemIcon>
                            <ListItemText primary={option} primaryTypographyProps={{ sx: { fontWeight: option === question.correctAnswer ? 600 : 400, color: option === question.correctAnswer ? COLORS.success : COLORS.dark } }} />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : null;

      default:
        return 'Paso desconocido';
    }
  };
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fffe', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`, borderRadius: 4, p: { xs: 3, md: 4 }, mb: 4, position: 'relative', overflow: 'hidden', boxShadow: `0 20px 60px ${alpha(COLORS.purple, 0.3)}` }}>
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: '40%', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AutoAwesomeIcon sx={{ fontSize: 36, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 900, letterSpacing: -0.5 }}>Generador Automático</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>Crea cursos completos con inteligencia artificial</Typography>
            </Box>
          </Box>
        </Box>

        {/* Stepper */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, border: '2px solid #e0e0e0' }}>
          <Stepper activeStep={activeStep} connector={<CustomConnector />}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', background: index <= activeStep ? `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)` : '#e0e0e0', color: index <= activeStep ? 'white' : '#9e9e9e', transition: 'all 0.3s ease', boxShadow: index <= activeStep ? `0 4px 14px ${alpha(COLORS.purple, 0.3)}` : 'none' }}>
                      {index < activeStep ? <CheckCircleIcon /> : step.icon}
                    </Box>
                  )}
                >
                  <Typography sx={{ fontWeight: index === activeStep ? 700 : 500, color: index === activeStep ? COLORS.purple : '#636e72' }}>{step.label}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Content */}
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '2px solid #e0e0e0', mb: 3 }}>
          {renderStepContent(activeStep)}

          {isGenerating && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <SmartToyIcon sx={{ color: COLORS.purple, animation: 'pulse 1.5s infinite' }} />
                <Typography sx={{ fontWeight: 600, color: COLORS.dark }}>La IA está generando el curso...</Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5, bgcolor: alpha(COLORS.purple, 0.15), '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`, borderRadius: 5 } }} />
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: '#636e72' }}>{progress}% completado</Typography>
            </Box>
          )}
        </Paper>

        {/* Navigation */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '2px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} disabled={activeStep === 0 || isGenerating} onClick={() => setActiveStep(prev => prev - 1)} sx={{ borderColor: COLORS.primary, color: COLORS.primary, fontWeight: 600, borderRadius: 2, px: 3, '&:hover': { borderColor: COLORS.primary, bgcolor: alpha(COLORS.primary, 0.08) }, '&.Mui-disabled': { borderColor: '#e0e0e0', color: '#bdbdbd' } }}>
            Atrás
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {steps.map((_, index) => (
              <Box key={index} sx={{ width: index === activeStep ? 24 : 8, height: 8, borderRadius: 4, background: index <= activeStep ? `linear-gradient(90deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)` : '#e0e0e0', transition: 'all 0.3s ease' }} />
            ))}
          </Box>

          {activeStep === steps.length - 1 ? (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveCourse} disabled={isGenerating} sx={{ background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`, fontWeight: 700, borderRadius: 2, px: 4, py: 1.5, boxShadow: `0 4px 14px ${alpha(COLORS.gradient1, 0.4)}`, '&:hover': { boxShadow: `0 6px 20px ${alpha(COLORS.gradient1, 0.5)}` } }}>
              Guardar Curso
            </Button>
          ) : (
            <Button variant="contained" endIcon={activeStep === 0 ? <AutoAwesomeIcon /> : <ArrowForwardIcon />} onClick={activeStep === 0 ? handleGenerateCourse : () => setActiveStep(prev => prev + 1)} disabled={isGenerating || (activeStep === 0 && (!courseData.title || !courseData.category))} sx={{ background: activeStep === 0 ? `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)` : `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`, fontWeight: 700, borderRadius: 2, px: 4, boxShadow: `0 4px 14px ${alpha(activeStep === 0 ? COLORS.purple : COLORS.gradient1, 0.4)}`, '&:hover': { boxShadow: `0 6px 20px ${alpha(activeStep === 0 ? COLORS.purple : COLORS.gradient1, 0.5)}` }, '&.Mui-disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
              {activeStep === 0 ? 'Generar con IA' : 'Siguiente'}
            </Button>
          )}
        </Paper>
      </Container>

      {/* Dialog Biblioteca */}
      <Dialog open={libraryOpen} onClose={() => setLibraryOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, ' + COLORS.purple + ' 0%, ' + COLORS.primary + ' 100%)', color: 'white', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FolderOpenIcon /> Biblioteca del Servidor
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {libraryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LinearProgress sx={{ width: '80%' }} />
            </Box>
          ) : (
            <Box>
              {getLibraryFilesForTarget().length === 0 ? (
                <Typography sx={{ py: 3, textAlign: 'center', color: '#636e72' }}>No hay archivos disponibles</Typography>
              ) : (
                getLibraryFilesForTarget().map((file, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 1, borderRadius: 2, border: '1px solid #e0e0e0', cursor: 'pointer', '&:hover': { bgcolor: alpha(COLORS.purple, 0.05), borderColor: COLORS.purple } }} onClick={() => handleLibrarySelect(file)}>
                    {file.name.endsWith('.pdf') ? <PictureAsPdfIcon sx={{ color: COLORS.error }} /> : <ImageIcon sx={{ color: COLORS.success }} />}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{file.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#636e72' }}>{formatFileSize(file.size)}</Typography>
                    </Box>
                    <Chip label="Seleccionar" size="small" sx={{ bgcolor: alpha(COLORS.purple, 0.15), color: COLORS.purple, fontWeight: 600 }} />
                  </Box>
                ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLibraryOpen(false)} sx={{ color: '#636e72' }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </Box>
  );
};

export default AutoCourseGenerator;